import { doc, getDoc, setDoc } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { DEFAULT_CREATE_PHRASES } from './lineCreatePhrases'

export type LineAttachmentScopeCap = {
  maxTasksPerGoal: number
  maxSubtasksPerTask: number
  maxChecklistsPerContainer: number
}

export interface LineLearningWorkspacePolicy {
  signalCaptureEnabled: boolean
  globalKillSwitch: boolean
  liveKnobsEnabled: boolean
  rawSignalRetentionDays: 30 | 60 | 90 | 180 | -1
  warmupMinSignals: number
  clusterMergeThreshold: number
  evalPassThreshold: number
  autoApproveDeltaThreshold: number
  clusterKillSwitches: Record<string, boolean>
  captureNewWorkspaceDefault: boolean
  approveDigestFrequency: 'realtime' | 'hourly' | 'daily'
  anonymisationLevel: 'none' | 'hashed-ws' | 'hashed-ws-and-user'
  signalWeights: { immediateBuild: number; cleanDraft: number; laggingUsage: number }
}

export interface LineWorkspacePolicy {
  interviewScaleTaskThreshold: number
  interviewScaleChecklistThreshold: number
  interviewAttachmentConflictRatio: number
  attachmentInventoryEnabled: boolean
  attachmentScopeCap: LineAttachmentScopeCap
  createPhrases: string[]
  pendingProposalTtlMs: number
  discardUndoToastMs: number
  learning: LineLearningWorkspacePolicy
  updatedAt: number
  updatedBy: string
}

export const DEFAULT_LINE_WORKSPACE_POLICY: LineWorkspacePolicy = {
  interviewScaleTaskThreshold: 4,
  interviewScaleChecklistThreshold: 12,
  interviewAttachmentConflictRatio: 2,
  attachmentInventoryEnabled: true,
  attachmentScopeCap: {
    maxTasksPerGoal: 0,
    maxSubtasksPerTask: 0,
    maxChecklistsPerContainer: 0,
  },
  createPhrases: [...DEFAULT_CREATE_PHRASES],
  pendingProposalTtlMs: 30 * 24 * 60 * 60 * 1000,
  discardUndoToastMs: 5_000,
  learning: {
    signalCaptureEnabled: true,
    globalKillSwitch: false,
    liveKnobsEnabled: false,
    rawSignalRetentionDays: 30,
    warmupMinSignals: 50,
    clusterMergeThreshold: 10,
    evalPassThreshold: 0.95,
    autoApproveDeltaThreshold: 0.1,
    clusterKillSwitches: {},
    captureNewWorkspaceDefault: true,
    approveDigestFrequency: 'daily',
    anonymisationLevel: 'none',
    signalWeights: { immediateBuild: 1, cleanDraft: 2, laggingUsage: 4 },
  },
  updatedAt: 0,
  updatedBy: '',
}

const cache = new Map<string, LineWorkspacePolicy>()

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

const integer = (value: unknown, fallback: number, min: number, max: number) => {
  const parsed = Math.round(Number(value))
  return Number.isSafeInteger(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback
}

const decimal = (value: unknown, fallback: number, min: number, max: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback
}

const phrases = (value: unknown) => {
  const result = Array.isArray(value)
    ? value.filter((phrase): phrase is string => typeof phrase === 'string')
      .map((phrase) => phrase.trim().toLocaleLowerCase())
      .filter(Boolean)
      .slice(0, 30)
    : []
  return result.length ? [...new Set(result)] : [...DEFAULT_CREATE_PHRASES]
}

export const normalizeLineWorkspacePolicy = (value: unknown): LineWorkspacePolicy => {
  const source = asRecord(value)
  const caps = asRecord(source.attachmentScopeCap)
  const learning = asRecord(source.learning)
  return {
    interviewScaleTaskThreshold: integer(source.interviewScaleTaskThreshold, DEFAULT_LINE_WORKSPACE_POLICY.interviewScaleTaskThreshold, 1, 100),
    interviewScaleChecklistThreshold: integer(source.interviewScaleChecklistThreshold, DEFAULT_LINE_WORKSPACE_POLICY.interviewScaleChecklistThreshold, 1, 500),
    interviewAttachmentConflictRatio: decimal(source.interviewAttachmentConflictRatio, DEFAULT_LINE_WORKSPACE_POLICY.interviewAttachmentConflictRatio, 1, 20),
    attachmentInventoryEnabled: source.attachmentInventoryEnabled !== false,
    attachmentScopeCap: {
      maxTasksPerGoal: integer(caps.maxTasksPerGoal, 0, 0, 100),
      maxSubtasksPerTask: integer(caps.maxSubtasksPerTask, 0, 0, 100),
      maxChecklistsPerContainer: integer(caps.maxChecklistsPerContainer, 0, 0, 500),
    },
    createPhrases: phrases(source.createPhrases),
    pendingProposalTtlMs: integer(source.pendingProposalTtlMs, DEFAULT_LINE_WORKSPACE_POLICY.pendingProposalTtlMs, 60_000, 90 * 24 * 60 * 60 * 1000),
    discardUndoToastMs: integer(source.discardUndoToastMs, DEFAULT_LINE_WORKSPACE_POLICY.discardUndoToastMs, 1_000, 60_000),
    learning: {
      signalCaptureEnabled: learning.signalCaptureEnabled !== false,
      globalKillSwitch: learning.globalKillSwitch === true,
      liveKnobsEnabled: learning.liveKnobsEnabled === true,
      rawSignalRetentionDays: ([30, 60, 90, 180, -1] as number[]).includes(Number(learning.rawSignalRetentionDays)) ? Number(learning.rawSignalRetentionDays) as LineLearningWorkspacePolicy['rawSignalRetentionDays'] : 30,
      warmupMinSignals: integer(learning.warmupMinSignals, 50, 1, 10_000),
      clusterMergeThreshold: integer(learning.clusterMergeThreshold, 10, 1, 10_000),
      evalPassThreshold: decimal(learning.evalPassThreshold, 0.95, 0, 1),
      autoApproveDeltaThreshold: decimal(learning.autoApproveDeltaThreshold, 0.1, 0, 1),
      clusterKillSwitches: Object.fromEntries(Object.entries(asRecord(learning.clusterKillSwitches)).filter(([key, enabled]) => key.length <= 240 && enabled === true)) as Record<string, boolean>,
      captureNewWorkspaceDefault: learning.captureNewWorkspaceDefault !== false,
      approveDigestFrequency: learning.approveDigestFrequency === 'realtime' || learning.approveDigestFrequency === 'hourly' ? learning.approveDigestFrequency : 'daily',
      anonymisationLevel: learning.anonymisationLevel === 'hashed-ws' || learning.anonymisationLevel === 'hashed-ws-and-user'
        ? learning.anonymisationLevel
        : 'none',
      signalWeights: {
        immediateBuild: decimal(asRecord(learning.signalWeights).immediateBuild, 1, 0, 10),
        cleanDraft: decimal(asRecord(learning.signalWeights).cleanDraft, 2, 0, 10),
        laggingUsage: decimal(asRecord(learning.signalWeights).laggingUsage, 4, 0, 10),
      },
    },
    updatedAt: integer(source.updatedAt, 0, 0, Number.MAX_SAFE_INTEGER),
    updatedBy: typeof source.updatedBy === 'string' ? source.updatedBy.slice(0, 256) : '',
  }
}

const policyRef = (workspaceId: string) => doc(firestoreDb, 'workspaces', workspaceId, 'settings', 'line_policy')

/** Returns the session-cached policy. A failed policy read deliberately falls back to the shipped defaults. */
export const getLineWorkspacePolicy = async (workspaceId: string): Promise<LineWorkspacePolicy> => {
  if (!workspaceId) return normalizeLineWorkspacePolicy({})
  const cached = cache.get(workspaceId)
  if (cached) return cached
  try {
    const snapshot = await getDoc(policyRef(workspaceId))
    const policy = normalizeLineWorkspacePolicy(snapshot.exists() ? snapshot.data() : {})
    cache.set(workspaceId, policy)
    return policy
  } catch {
    return normalizeLineWorkspacePolicy({})
  }
}

export const getCachedLineWorkspacePolicy = (workspaceId: string) =>
  cache.get(workspaceId) ?? normalizeLineWorkspacePolicy({})

export const invalidatePolicyCache = (workspaceId: string) => {
  cache.delete(workspaceId)
}

export const saveLineWorkspacePolicy = async (workspaceId: string, value: unknown, actorUid: string) => {
  const policy = normalizeLineWorkspacePolicy(value)
  const patch: LineWorkspacePolicy = {
    ...policy,
    updatedAt: Date.now(),
    updatedBy: actorUid,
  }
  await setDoc(policyRef(workspaceId), patch)
  invalidatePolicyCache(workspaceId)
  return patch
}
